import { showNotification } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import useTranslation from 'next-translate/useTranslation';
import get from 'lodash.get';
import { useContext, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { match } from 'ts-pattern';
import { v4 as uuidv4 } from 'uuid';

import { PaymentType } from '~/common/enums/payments.enum';
import { CurrencyConvertor } from '~/components/utils/formatters';
import useOpenIntercom from '~/components/Intercom/useOpenItercom';
import { mailToSupport } from '~/components/utils/mailToSupport';
import { UserContext } from '~/components/providers/UserProvider';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { Routes } from '~/domains/common/constants/routes';
import { storeReturnUrl } from '~/domains/kyc/utils/kycReturnUrl';
import { reportEvent } from '~/domains/analytics';
import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { getWithdrawalEligibility, withdrawFromWallet } from '~/services/wallet/wallet.service.api';
import log, { logError } from '~/common/utils/log';
import { getErrorTranslations } from '~/domains/payments/utils/utils';

import { useWithdrawalFormContext } from '../context/withdrawal.context';
import { validateWithdrawalForm } from '../utils/validateWithdrawalForm';
import type { WithdrawalGateDecision } from '../utils/withdrawalGate';
import {
  WITHDRAWAL_GATE_UNREACHABLE_DECISION,
  decideWithdrawalGate,
  toAmountCents,
} from '../utils/withdrawalGate';

import { useWithdrawalLocationErrorHandler } from './useWithdrawalLocationErrorHandler';
import { useWithdrawalWithVIPP } from './useWithdrawalWithVIPP';

const useSubmitWithdrawal = () => {
  const { t } = useTranslation('payments');

  const form = useWithdrawalFormContext();
  const merchantRefNum = uuidv4();
  const { refetch: getUserBalance, data: balance } = useWalletBalance();
  const { openIntercom, isIntercomAvailable } = useOpenIntercom();
  const { refreshUserSsnStatus } = useContext(UserContext);
  const { closeModalsByType } = useContext(RootModalsContext);
  const handleLocationError = useWithdrawalLocationErrorHandler();
  const latestSubmitWithdrawal = useRef<() => Promise<void>>();
  const router = useRouter();

  const { paymentType, cardOption, bankAccountId } = form.values;

  const withdrawalAmount = form.values.withdrawalAmount * 1;

  const values = {
    merchantRefNum,
    amount: withdrawalAmount,
    paymentType: [
      PaymentType.PAYPAL,
      PaymentType.VIPPREFERRED,
      PaymentType.VENMO,
      PaymentType.SKRILL,
      PaymentType.AEROPAY,
    ].includes(paymentType as PaymentType)
      ? paymentType
      : PaymentType.CARD,
    ...(![
      PaymentType.PAYPAL,
      PaymentType.VIPPREFERRED,
      PaymentType.VENMO,
      PaymentType.SKRILL,
      PaymentType.AEROPAY,
    ].includes(paymentType as PaymentType) && {
      paymentHandleId: cardOption,
      currency: 'USD',
      apiVersion: 2,
    }),
    ...(paymentType === PaymentType.AEROPAY && {
      paymentHandleId: bankAccountId,
    }),
  };

  const getVIPPToken = useWithdrawalWithVIPP({
    amount: values.amount,
    merchantRefNum,
  });
  const withdrawViaCheck = ({ amount }) => {
    const message = t('withdrawal.form.actions.withdrawToCheck.message', {
      amount: CurrencyConvertor(amount),
      balance: CurrencyConvertor(balance?.availableBalance),
    });
    if (isIntercomAvailable) {
      openIntercom(message);
    } else {
      mailToSupport({
        subject: t('withdrawal.form.actions.withdrawToCheck.mailTitle'),
        body: t('withdrawal.form.actions.withdrawToCheck.mailMessage', {
          message,
        }),
      });
    }
  };

  const withdrawal = useMutation({
    mutationKey: ['withdrawal'],
    mutationFn: async (val: {
      paymentHandleId?: string;
      paymentHandleToken?: string;
      currency: string;
      apiVersion: number;
      merchantRefNum: string;
      amount: number;
      paymentType: PaymentType;
      bankAccountId?: string;
    }) => {
      if (paymentType === PaymentType.AEROPAY && bankAccountId === undefined) {
        throw new Error('Linking new bank account not handled yet');
      }

      if (paymentType === PaymentType.PAPER_CHECK) {
        log('Withdraw - check');
        return withdrawViaCheck(val);
      }

      if (paymentType === PaymentType.VIPPREFERRED) {
        log('Withdraw - online banking');

        try {
          const vippToken = await getVIPPToken();
          val.paymentHandleToken = vippToken;
        } catch (error) {
          logError(error);
        }
      }

      return withdrawFromWallet(val);
    },
    onSuccess: (response) => {
      if (response?.data?.status === 'COMPLETED') {
        getUserBalance();

        reportEvent('Transactions > Withdraw', {
          withdrawal_id: response.id,
          withdrawal_amount: withdrawalAmount,
          withdrawal_method: paymentType,
        });

        form.getInputProps('completedWithdrawalAmount').onChange(withdrawalAmount);
      }

      if (response?.data?.status === 'PROCESSING') {
        getUserBalance();

        reportEvent('Transactions > Withdraw Processing', {
          withdrawal_id: response.id,
          withdrawal_amount: withdrawalAmount,
          withdrawal_method: paymentType,
        });

        form.getInputProps('completedWithdrawalAmount').onChange(withdrawalAmount);
      }
    },
    onError: (error: unknown) => {
      logError(error);

      if (handleLocationError(error, () => latestSubmitWithdrawal.current?.())) {
        return;
      }

      const isApiError = axios.isAxiosError(error);
      const errorCode: string = isApiError ? get(error, 'code', '') : '';
      const rawMessage: unknown = isApiError ? get(error, 'response.data.message') : undefined;
      const backendMessage = typeof rawMessage === 'string' ? rawMessage : '';
      const errorMessage = getErrorTranslations(t, errorCode);

      if (backendMessage.toLowerCase().includes('w9')) {
        refreshUserSsnStatus();
      }

      if (errorCode === 'ERR_BAD_REQUEST') {
        errorMessage.title = t('errors.generic.withdrawal.title');
        errorMessage.message = backendMessage;
      }

      showNotification({
        title: errorMessage.title,
        message: errorMessage.message,
        color: 'red',
        autoClose: 5000,
      });
    },
  });

  const { mutateAsync: checkEligibility, isLoading: isCheckingEligibility } = useMutation({
    mutationKey: ['withdrawal-eligibility'],
    mutationFn: (amountCents: number) => getWithdrawalEligibility(amountCents),
  });

  const notifyGateRefusal = (message: string) => {
    showNotification({
      title: t('errors.generic.withdrawal.title'),
      message,
      color: 'red',
      autoClose: 5000,
    });
  };

  const submitWithdrawal = async () => {
    const isValidated = validateWithdrawalForm(paymentType, form.validateField);
    if (!isValidated || withdrawal.isLoading || isCheckingEligibility) {
      return;
    }

    const amountCents = toAmountCents(withdrawalAmount);

    let decision: WithdrawalGateDecision;
    try {
      const eligibility = await checkEligibility(amountCents);
      decision = decideWithdrawalGate(eligibility, amountCents);
    } catch (error) {
      logError(error);
      decision = WITHDRAWAL_GATE_UNREACHABLE_DECISION;
    }

    match(decision)
      .with('proceed', () => {
        withdrawal.mutate(values);
      })
      .with('w9', () => {
        closeModalsByType('WithdrawalModal');
        router.push(Routes.withdrawTaxInformation());
      })
      .with('indeterminate', () => {
        notifyGateRefusal(t('errors.generic.withdrawal.eligibilityUnavailable'));
      })
      .with('both', () => {
        closeModalsByType('WithdrawalModal');
        storeReturnUrl(Routes.withdrawRequirements(), withdrawalAmount);
        router.push(Routes.withdrawRequirements());
      })
      .with('docv', () => {
        closeModalsByType('WithdrawalModal');
        storeReturnUrl(Routes.withdraw(), withdrawalAmount);
        router.push(Routes.withdrawVerifyIdentity());
      })
      .exhaustive();
  };

  useEffect(() => {
    latestSubmitWithdrawal.current = submitWithdrawal;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitWithdrawal();
  };

  return { handleSubmit, isWithdrawing: withdrawal.isLoading || isCheckingEligibility };
};

export default useSubmitWithdrawal;
